import { useCallback, useEffect, useRef, useState } from 'react';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { Num, CartaoIndicador } from '../../../components/gps';
import TituloTela from '../../../components/gps/TituloTela';
import { isMaster } from '../../../utils/perfil';
import { cn } from '../../../lib/utils';

/* ============================================================================
   Monitoramento — Configurações, somente Master.

   Duas abas: "Servidor" (CPU, memória, disco e processos do pm2) e "Banco de
   dados" (arquivos, maiores tabelas e conexões). Cada bloco da API pode vir
   como `{ erro }` sem derrubar os outros; nesse caso a tela mostra a mensagem
   no lugar do bloco - nunca um zero, que passaria por "tudo bem".

   A permissão que vale é a da rota no servidor (`/logs/recursos` e
   `/logs/banco`); o menu e a checagem abaixo só evitam oferecer o que não
   se pode usar.
   ============================================================================ */

type Erro = { erro: string };

type Recursos = {
  servidor: { nome: string; sistema: string; node: string; ligadoHaSegundos: number };
  cpu: { percentual: number; nucleos: number; modelo: string } | Erro;
  memoria: { totalBytes: number; usadaBytes: number; percentual: number } | Erro;
  disco: { totalBytes: number; usadoBytes: number; percentual: number } | Erro;
  api: { memoriaBytes: number; heapBytes: number; ligadaHaSegundos: number };
  processos: Array<{ nome: string; estado: string; cpu: number; memoriaBytes: number; reinicios: number; desde: string }> | Erro;
};

type Banco = {
  info: { banco: string; versao: string; edicao: string } | Erro;
  arquivos: Array<{ arquivo: string; tipo: string; tamanhoBytes: number; usadoBytes: number }> | Erro;
  tabelas: Array<{ tabela: string; linhas: number; tamanhoBytes: number }> | Erro;
  conexoes: Array<{ estado: string; quantidade: number }> | Erro;
};

const temErro = (x: unknown): x is Erro => !!x && !Array.isArray(x) && typeof (x as Erro).erro === 'string';

function bytes(n: number) {
  if (!Number.isFinite(n)) return '—';
  const un = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < un.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: i === 0 ? 0 : 1 })} ${un[i]}`;
}

function duracao(seg: number) {
  if (!Number.isFinite(seg)) return '—';
  const d = Math.floor(seg / 86400);
  const h = Math.floor((seg % 86400) / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}

const pct = (n: number) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

// Verde até 70%, âmbar até 90%, vermelho acima: o tom avisa antes de acabar.
const tomUso = (p: number) => (p >= 90 ? 'danger' : p >= 70 ? 'warn' : 'ok');

function AvisoErro({ rotulo, mensagem }: { rotulo: string; mensagem: string }) {
  return (
    <div className="rounded-lg border border-danger bg-danger-soft p-3 text-[12.5px] text-danger">
      <b>{rotulo}:</b> {mensagem}
    </div>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <h2 className="mb-2 text-[12px] font-bold uppercase tracking-[0.07em] text-muted-foreground">{titulo}</h2>
      {children}
    </section>
  );
}

const CABECALHO = 'bg-primary px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-primary-foreground';

function Tabela({ colunas, linhas }: { colunas: string[]; linhas: React.ReactNode[][] }) {
  const grade = { gridTemplateColumns: `repeat(${colunas.length}, minmax(0, 1fr))` };
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface shadow-card">
      <div className={cn('grid items-center gap-2', CABECALHO)} style={grade}>
        {colunas.map((c) => (
          <div key={c}>{c}</div>
        ))}
      </div>
      {linhas.length === 0 && <div className="p-6 text-center text-[12.5px] text-muted-foreground">Nada a mostrar.</div>}
      {linhas.map((l, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className="grid items-center gap-2 border-b px-3 py-1.5 text-[12px]"
          style={{ ...grade, borderColor: 'var(--gps-soft)' }}
        >
          {l.map((c, j) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={j} className="min-w-0 truncate">
              {c}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

Monitoramento.getLayout = function getLayout(pagina: React.ReactElement) {
  return <Layout>{pagina}</Layout>;
};

export default function Monitoramento() {
  const { user } = useAuth();
  const master = isMaster(user);

  const [aba, setAba] = useState<'servidor' | 'banco'>('servidor');
  const [recursos, setRecursos] = useState<Recursos | null>(null);
  const [banco, setBanco] = useState<Banco | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [auto, setAuto] = useState(false);

  const carregar = useCallback(async () => {
    if (!master) return;
    setCarregando(true);
    try {
      if (aba === 'servidor') {
        const r = await axios.get<Recursos>('/logs/recursos');
        setRecursos(r.data);
      } else {
        const r = await axios.get<Banco>('/logs/banco');
        setBanco(r.data);
      }
      setErro('');
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível ler o monitoramento.');
    } finally {
      setCarregando(false);
    }
  }, [master, aba]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // `ref` para o intervalo não ser recriado a cada render (mesmo motivo da tela de logs).
  const refCarregar = useRef(carregar);
  refCarregar.current = carregar;

  useEffect(() => {
    if (!auto || !master || aba !== 'servidor') return undefined;
    const id = setInterval(() => refCarregar.current(), 10000);
    return () => clearInterval(id);
  }, [auto, master, aba]);

  if (!master) {
    return (
      <Page title="Monitoramento">
        <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 py-10">
          <div className="rounded-lg border border-warn bg-warn-soft p-4 text-[12.5px]">
            Esta tela é restrita ao perfil Master.
          </div>
        </div>
      </Page>
    );
  }

  const { cpu, memoria, disco } = recursos || ({} as Partial<Recursos>);

  return (
    <Page title="Monitoramento">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <TituloTela descricao="Uso do servidor e do banco de dados neste momento.">Monitoramento</TituloTela>

          <div className="flex flex-wrap items-center gap-2">
            {aba === 'servidor' && (
              <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
                Atualizar sozinho (10s)
              </label>
            )}
            <button
              type="button"
              onClick={carregar}
              disabled={carregando}
              className="h-8 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
            >
              {carregando ? 'Lendo...' : '↻ Atualizar'}
            </button>
          </div>
        </div>

        <div role="tablist" aria-label="Fonte do monitoramento" className="mb-3 flex gap-1 border-b border-line">
          {[
            { id: 'servidor' as const, rotulo: 'Servidor' },
            { id: 'banco' as const, rotulo: 'Banco de dados' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={aba === t.id}
              onClick={() => {
                setErro('');
                setAba(t.id);
              }}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-[12.5px] font-semibold',
                aba === t.id ? 'border-primary text-ink' : 'border-transparent text-muted-foreground hover:text-ink'
              )}
            >
              {t.rotulo}
            </button>
          ))}
        </div>

        {erro && (
          <div className="mb-3 rounded-lg border border-danger bg-danger-soft p-3 text-[12.5px] text-danger">{erro}</div>
        )}

        {/* ------------------------------------------------------------ SERVIDOR */}
        {aba === 'servidor' && recursos && (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {temErro(cpu) ? (
                <AvisoErro rotulo="CPU" mensagem={cpu.erro} />
              ) : cpu ? (
                <CartaoIndicador
                  rotulo="CPU"
                  valor={pct(cpu.percentual)}
                  legenda={`${cpu.nucleos} núcleos`}
                  proporcao={cpu.percentual / 100}
                  tom={tomUso(cpu.percentual)}
                />
              ) : null}

              {temErro(memoria) ? (
                <AvisoErro rotulo="Memória" mensagem={memoria.erro} />
              ) : memoria ? (
                <CartaoIndicador
                  rotulo="Memória"
                  valor={pct(memoria.percentual)}
                  legenda={`${bytes(memoria.usadaBytes)} de ${bytes(memoria.totalBytes)}`}
                  proporcao={memoria.percentual / 100}
                  tom={tomUso(memoria.percentual)}
                />
              ) : null}

              {temErro(disco) ? (
                <AvisoErro rotulo="Disco" mensagem={disco.erro} />
              ) : disco ? (
                <CartaoIndicador
                  rotulo="Disco"
                  valor={pct(disco.percentual)}
                  legenda={`${bytes(disco.usadoBytes)} de ${bytes(disco.totalBytes)}`}
                  proporcao={disco.percentual / 100}
                  tom={tomUso(disco.percentual)}
                />
              ) : null}

              <CartaoIndicador
                rotulo="Memória da API"
                valor={bytes(recursos.api.memoriaBytes)}
                legenda={`heap ${bytes(recursos.api.heapBytes)} · ligada há ${duracao(recursos.api.ligadaHaSegundos)}`}
              />
            </div>

            <Bloco titulo="Servidor">
              <div className="grid grid-cols-1 gap-x-6 gap-y-1 rounded-lg border border-line bg-surface p-3 text-[12.5px] sm:grid-cols-2">
                <div>
                  <span className="text-muted-foreground">Nome: </span>
                  {recursos.servidor.nome}
                </div>
                <div>
                  <span className="text-muted-foreground">Sistema: </span>
                  {recursos.servidor.sistema}
                </div>
                <div>
                  <span className="text-muted-foreground">Node: </span>
                  {recursos.servidor.node}
                </div>
                <div>
                  <span className="text-muted-foreground">Ligado há: </span>
                  {duracao(recursos.servidor.ligadoHaSegundos)}
                </div>
                {!temErro(cpu) && cpu && (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground">Processador: </span>
                    {cpu.modelo}
                  </div>
                )}
              </div>
            </Bloco>

            <Bloco titulo="Processos (pm2)">
              {temErro(recursos.processos) ? (
                <AvisoErro rotulo="pm2" mensagem={recursos.processos.erro} />
              ) : (
                <Tabela
                  colunas={['Nome', 'Estado', 'CPU', 'Memória', 'Reinícios', 'Desde']}
                  linhas={recursos.processos.map((p) => [
                    <b key="n">{p.nome}</b>,
                    p.estado,
                    <Num key="c">{pct(p.cpu)}</Num>,
                    <Num key="m">{bytes(p.memoriaBytes)}</Num>,
                    <Num key="r">{p.reinicios}</Num>,
                    p.desde ? new Date(p.desde).toLocaleString('pt-BR') : '—',
                  ])}
                />
              )}
            </Bloco>
          </>
        )}

        {/* ------------------------------------------------------- BANCO DE DADOS */}
        {aba === 'banco' && banco && (
          <>
            <Bloco titulo="Banco">
              {temErro(banco.info) ? (
                <AvisoErro rotulo="Banco" mensagem={banco.info.erro} />
              ) : (
                <div className="grid grid-cols-1 gap-x-6 gap-y-1 rounded-lg border border-line bg-surface p-3 text-[12.5px] sm:grid-cols-3">
                  <div>
                    <span className="text-muted-foreground">Nome: </span>
                    {banco.info.banco}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Versão: </span>
                    {banco.info.versao}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Edição: </span>
                    {banco.info.edicao}
                  </div>
                </div>
              )}
            </Bloco>

            <Bloco titulo="Arquivos">
              {temErro(banco.arquivos) ? (
                <AvisoErro rotulo="Arquivos" mensagem={banco.arquivos.erro} />
              ) : (
                <Tabela
                  colunas={['Arquivo', 'Tipo', 'Tamanho', 'Usado']}
                  linhas={banco.arquivos.map((a) => [
                    a.arquivo,
                    a.tipo,
                    <Num key="t">{bytes(a.tamanhoBytes)}</Num>,
                    <Num key="u">{bytes(a.usadoBytes)}</Num>,
                  ])}
                />
              )}
            </Bloco>

            <Bloco titulo="20 maiores tabelas">
              {temErro(banco.tabelas) ? (
                <AvisoErro rotulo="Tabelas" mensagem={banco.tabelas.erro} />
              ) : (
                <Tabela
                  colunas={['Tabela', 'Linhas', 'Tamanho']}
                  linhas={banco.tabelas.map((t) => [
                    <b key="n">{t.tabela}</b>,
                    <Num key="l">{t.linhas.toLocaleString('pt-BR')}</Num>,
                    <Num key="t">{bytes(t.tamanhoBytes)}</Num>,
                  ])}
                />
              )}
            </Bloco>

            <Bloco titulo="Conexões por estado">
              {temErro(banco.conexoes) ? (
                <AvisoErro rotulo="Conexões" mensagem={banco.conexoes.erro} />
              ) : (
                <Tabela
                  colunas={['Estado', 'Quantidade']}
                  linhas={banco.conexoes.map((c) => [c.estado, <Num key="q">{c.quantidade.toLocaleString('pt-BR')}</Num>])}
                />
              )}
            </Bloco>
          </>
        )}
      </div>
    </Page>
  );
}
